/**
 * The row a menu item form writes.
 *
 * Pure on purpose, and separate from the screen: what a form writes should be
 * provable without a browser. The editor was built with two layouts so they
 * could be A/B tested against each other, and this is what made that comparison
 * mean anything — the same edits produce the same row, or the difference is the
 * bug. It stays the single definition of the row now that one layout remains.
 *
 * Every rule the old editor had is here, not in a component:
 *
 *   - a dish priced by a group still stores a number, because the column is
 *     `not null`, and it stores the group's cheapest choice — which is also
 *     what the menu shows;
 *   - inventory is null unless it is tracked, so an untracked item cannot carry
 *     a stale count;
 *   - prep time and smallest order are clamped rather than validated, because
 *     a value out of range is not worth refusing a save over.
 */

export interface ItemPayloadInput {
  /** Where it sits; null for Uncategorised. */
  categoryId: string | null;
  name: string;
  description: string;
  /** The typed price, parsed. Null when the groups price the dish instead. */
  priceCents: number | null;
  /** True when a group sets the price rather than adding to it. */
  pricedByGroup: boolean;
  /** That group's cheapest choice, used when it does. */
  cheapestGroupPrice: number;
  /** Resolved before this is called: an upload is a side effect, not a mapping. */
  photoUrl: string | null;
  photoPath: string | null;
  isActive: boolean;
  trackInventory: boolean;
  inventoryCount: string;
  prepMinutes: string;
  minQuantity: string;
}

/** The columns a form may write. */
export interface ItemPayload {
  category_id: string | null;
  name: string;
  description: string | null;
  price_cents: number;
  photo_url: string | null;
  photo_path: string | null;
  is_active: boolean;
  track_inventory: boolean;
  inventory_count: number | null;
  prep_minutes_baseline: number;
  min_quantity: number;
}

/**
 * Builds the row for a menu item.
 *
 * @param input The form's values, already parsed.
 * @returns The columns to write.
 */
export function buildItemPayload(input: ItemPayloadInput): ItemPayload {
  return {
    category_id: input.categoryId,
    name: input.name.trim(),
    description: input.description.trim() || null,
    // The column is `not null`, so a dish priced by a group holds that group's
    // cheapest choice — which is also the price the menu shows.
    price_cents: input.pricedByGroup ? input.cheapestGroupPrice : (input.priceCents ?? 0),
    photo_url: input.photoUrl,
    photo_path: input.photoPath,
    is_active: input.isActive,
    track_inventory: input.trackInventory,
    inventory_count: input.trackInventory
      ? Math.max(0, Math.trunc(Number(input.inventoryCount)))
      : null,
    prep_minutes_baseline: Math.max(0, Math.trunc(Number(input.prepMinutes) || 0)),
    min_quantity: Math.min(99, Math.max(1, Math.trunc(Number(input.minQuantity) || 1))),
  };
}
